<script setup>
/**
 * VibeVox: фирменная шапка сайдбара — 1:1 с приложением VibeVox.
 *  - wordmark-логотип (светлая тема → тёмный текст, тёмная → фирменный цветной),
 *  - кнопка выбора языка (глобус) → ВСЕ 108 языков VibeVox + поиск, мгновенное переключение,
 *  - переключатель темы (солнце/луна) поверх штатного механизма Chatwoot
 *    (класс .dark на body + localStorage COLOR_SCHEME).
 *
 * Список языков синхронизирован с основным приложением (vibevoxLanguages.js, 108 шт.).
 * UI Chatwoot переведён пока не на все 108 — для непереведённых локалей интерфейс
 * показывается на English (fallbackLocale: 'en' в entrypoints/dashboard.js), пока не
 * добавим перевод. Переключатель меняет только клиентскую локаль (vue-i18n + localStorage).
 */
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { setColorTheme } from 'dashboard/helper/themeHelper';
import {
  VIBEVOX_LANGUAGES,
  vibevoxFilterLanguages,
} from 'dashboard/constants/vibevoxLanguages';
// VibeVox: ленивая загрузка локали (только активный язык, не все 110). См. runtime.js.
import { loadLocale } from 'dashboard/i18n/runtime';
import logoLight from 'dashboard/assets/images/vibevox/vibevox-logo-light.png';
import logoDark from 'dashboard/assets/images/vibevox/vibevox-logo-dark.png';

const { locale } = useI18n();

// — Тема: переиспользуем механизм Chatwoot (.dark на body + COLOR_SCHEME) —
const isDark = ref(document.body.classList.contains('dark'));
const toggleTheme = () => {
  LocalStorage.set(LOCAL_STORAGE_KEYS.COLOR_SCHEME, isDark.value ? 'light' : 'dark');
  setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
  isDark.value = document.body.classList.contains('dark');
};

// — Язык: все 108 языков VibeVox + поиск —
const VIBEVOX_LOCALE_KEY = 'vibevox_locale';
// Базовый код языка → реальная локаль в бандле dashboard (где имя отличается).
// Напр. список даёт 'zh' (Китайский), а перевод лежит в локали 'zh_CN'.
const LOCALE_ALIAS = { zh: 'zh_CN' };
const resolveLocale = code => LOCALE_ALIAS[code] || code;

const langOpen = ref(false);
const query = ref('');
// Базовый код выбранного языка (для подсветки активного пункта в списке).
const current = ref('en');
const filteredLanguages = computed(() => vibevoxFilterLanguages(query.value));

const setLanguage = code => {
  current.value = code;
  loadLocale(resolveLocale(code));
  LocalStorage.set(VIBEVOX_LOCALE_KEY, code);
  langOpen.value = false;
  query.value = '';
};

const toggleLangMenu = () => {
  langOpen.value = !langOpen.value;
  if (!langOpen.value) query.value = '';
};

const closeLangMenu = () => {
  langOpen.value = false;
  query.value = '';
};

onMounted(() => {
  const saved = LocalStorage.get(VIBEVOX_LOCALE_KEY);
  if (saved && VIBEVOX_LANGUAGES.some(l => l.code === saved)) {
    current.value = saved;
    loadLocale(resolveLocale(saved));
  } else {
    // Выводим текущую локаль интерфейса в базовый код для подсветки.
    const base = (locale.value || 'en').split(/[-_]/)[0];
    current.value = VIBEVOX_LANGUAGES.some(l => l.code === base) ? base : 'en';
  }
});

const btnClass =
  'flex items-center justify-center size-8 rounded-lg outline outline-1 outline-n-weak text-n-slate-11 hover:bg-n-alpha-2 transition-colors';
</script>

<template>
  <div class="flex gap-2 items-center px-2 pt-1 pb-3 mb-1 border-b border-n-weak">
    <!-- Лого VibeVox: светлая тема — тёмный, тёмная — фирменный цветной. Клик → vibevox.pro -->
    <a
      href="https://vibevox.pro/"
      target="_blank"
      rel="noopener noreferrer"
      title="vibevox.pro"
      class="flex items-center hover:opacity-80 transition-opacity"
    >
      <img :src="logoLight" alt="VibeVox" class="block w-auto h-6 dark:hidden" />
      <img :src="logoDark" alt="VibeVox" class="hidden w-auto h-6 dark:block" />
    </a>
    <div class="flex-grow" />

    <!-- Выбор языка: все 108 языков VibeVox + поиск сверху -->
    <div v-on-click-outside="closeLangMenu" class="relative">
      <button :class="btnClass" title="Language" @click="toggleLangMenu">
        <span class="i-lucide-globe size-4" />
      </button>
      <div
        v-if="langOpen"
        class="absolute z-50 mt-1 w-60 max-w-[calc(100vw-2rem)] rounded-xl border border-n-weak bg-n-solid-2 shadow-lg overflow-hidden ltr:left-0 rtl:right-0"
      >
        <!-- Поиск по языкам -->
        <div class="p-2 border-b border-n-weak">
          <div class="relative">
            <span
              class="i-lucide-search size-3.5 absolute top-1/2 -translate-y-1/2 left-2.5 rtl:left-auto rtl:right-2.5 text-n-slate-10 pointer-events-none"
            />
            <input
              v-model="query"
              type="text"
              aria-label="Поиск языка"
              class="w-full pl-8 pr-3 rtl:pl-3 rtl:pr-8 py-1.5 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12 outline-none focus:border-n-brand"
              autofocus
            />
          </div>
        </div>
        <!-- Список языков -->
        <div class="overflow-y-auto py-1 max-h-72">
          <p
            v-if="!filteredLanguages.length"
            class="px-3 py-2 text-sm text-n-slate-10"
          >
            Ничего не найдено
          </p>
          <button
            v-for="l in filteredLanguages"
            :key="l.code"
            class="flex justify-between items-center gap-2 w-full text-start px-3 py-1.5 text-sm hover:bg-n-alpha-2"
            :class="
              l.code === current
                ? 'bg-n-alpha-1 font-medium text-n-teal-11'
                : 'text-n-slate-12'
            "
            @click="setLanguage(l.code)"
          >
            <span class="truncate">{{ l.name }}</span>
            <span class="text-xs text-n-slate-10 flex-shrink-0">{{ l.en }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Переключатель темы -->
    <button :class="btnClass" :title="isDark ? 'Light' : 'Dark'" @click="toggleTheme">
      <span :class="isDark ? 'i-lucide-sun' : 'i-lucide-moon'" class="size-4" />
    </button>
  </div>
</template>
